import { cva, type VariantProps } from 'class-variance-authority';
import { disabledState, focusRing, transitionBase, uiMarker } from './shared';

/**
 * OPTION CARD
 *
 * A choice drawn as a card: pick a destination type, a company size, a
 * template, a plan. The product had this shape at fourteen call sites in ten
 * files, and every one of them spelled the selected state the same way —
 * `border-primary bg-primary/5` — while spelling everything around it
 * differently: border-2 or ring-2, rounded-lg or rounded-xl, p-4 or p-2, a
 * radio dot or a check badge or nothing.
 *
 * Surface has `interactive` for "the whole card is a link". This is the other
 * thing: a card that is one of several answers, and is either chosen or not.
 */
export const optionCardRecipe = cva(
  [
    uiMarker,
    'group relative flex w-full flex-col gap-2 border-2 text-left',
    transitionBase,
    focusRing,
    disabledState,
    'aria-disabled:pointer-events-none aria-disabled:opacity-50',
  ],
  {
    variants: {
      selected: {
        true: 'border-primary bg-primary/5',
        false: 'border-border bg-card hover:border-input',
      },
      padding: {
        sm: 'p-3',
        md: 'p-4',
      },
      radius: {
        lg: 'rounded-lg',
        xl: 'rounded-xl',
      },
    },
    defaultVariants: { selected: false, padding: 'md', radius: 'xl' },
  }
);

/**
 * The mark that says "this one". A radio dot when the choice is one-of, a
 * check when it is any-of — the same distinction the form controls make.
 */
export const optionCardIndicatorRecipe = cva(
  [uiMarker, 'flex shrink-0 items-center justify-center', transitionBase],
  {
    variants: {
      indicator: {
        radio: 'size-5 rounded-full border-2',
        check: 'size-5 rounded-full',
      },
      selected: { true: '', false: '' },
    },
    compoundVariants: [
      {
        indicator: 'radio',
        selected: true,
        class: 'border-primary bg-primary',
      },
      {
        indicator: 'radio',
        selected: false,
        class: 'border-muted-foreground/30',
      },
      {
        indicator: 'check',
        selected: true,
        class: 'bg-primary text-primary-foreground',
      },
      { indicator: 'check', selected: false, class: 'invisible' },
    ],
    defaultVariants: { indicator: 'radio', selected: false },
  }
);

export type OptionCardRecipeProps = VariantProps<typeof optionCardRecipe>;
export type OptionCardIndicatorRecipeProps = VariantProps<
  typeof optionCardIndicatorRecipe
>;
